import { useState, useCallback, useEffect, useRef } from 'react';
import { sendCode, refreshCaptcha } from '/src/api';

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  PRECONDITION_REQUIRED: 428,
  TOO_MANY_REQUESTS: 429
};

const ERROR_MESSAGES = {
  INVALID_CAPTCHA: '验证码错误',
  RATE_LIMITED: '验证码发送过于频繁，请稍后再试',
  REFRESH_FAILED: '刷新验证码失败',
  NETWORK_ERROR: '网络错误，请稍后重试',
  CANCELLED: '操作已取消'
};

const INITIAL_CAPTCHA_STATE = {
  visible: false,
  imageUrl: '',
  key: '',
  loading: false,
  error: '',
  pendingPhone: '',
  deferred: null
};

/**
 * 验证码功能的自定义 Hook
 * @param {Object} options 配置选项
 * @param {string} options.sendCodePath - 发送验证码的 API 路径 (如 'signup', 'login/phone')
 * @param {Function} options.onError - 错误处理函数
 * @returns {Object} 验证码相关的状态和方法
 */
export const useCaptcha = ({ sendCodePath, onError }) => {
  const [captchaState, setCaptchaState] = useState(INITIAL_CAPTCHA_STATE);
  const isMountedRef = useRef(true);
  const deferredRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (deferredRef.current) {
        try {
          deferredRef.current.reject?.(new Error(ERROR_MESSAGES.CANCELLED));
        } catch (_) {}
        deferredRef.current = null;
      }
    };
  }, []);

  const updateCaptchaState = useCallback((updates) => {
    if (!isMountedRef.current) return;
    setCaptchaState(prev => ({ ...prev, ...updates }));
  }, []);

  const hideCaptcha = useCallback(() => {
    deferredRef.current = null;
    updateCaptchaState({ ...INITIAL_CAPTCHA_STATE });
  }, [updateCaptchaState]);

  const doRefreshCaptcha = useCallback(async () => {
    if (captchaState.loading) return;
    try {
      updateCaptchaState({ loading: true, error: '' });
      const data = await refreshCaptcha();
      updateCaptchaState({
        key: data?.key || '',
        imageUrl: data?.image_url || '',
        loading: false
      });
    } catch (error) {
      updateCaptchaState({ loading: false, error: ERROR_MESSAGES.REFRESH_FAILED });
      console.error(ERROR_MESSAGES.REFRESH_FAILED, error);
      onError?.(error);
    }
  }, [captchaState.loading, updateCaptchaState, onError]);

  const getErrorMessage = useCallback((err) => {
    const data = err?.response?.data || err;
    return (data?.errors?.captcha_response?.[0]) || data?.detail || ERROR_MESSAGES.INVALID_CAPTCHA;
  }, []);

  const confirmCaptcha = useCallback(async (captchaResponse) => {
    if (!captchaState.deferred || captchaState.loading) return;

    const currentDeferred = captchaState.deferred;
    try {
      updateCaptchaState({ loading: true });
      await sendCode(sendCodePath, {
        phone: captchaState.pendingPhone,
        captcha_key: captchaState.key,
        captcha_response: captchaResponse,
      });
      currentDeferred?.resolve?.();
      hideCaptcha();
    } catch (err) {
      const status = err?.response?.status;

      if (status === HTTP_STATUS.PRECONDITION_REQUIRED) {
        const data = err?.response?.data?.data || {};
        const newKey = data.key || '';
        const newImage = data.image_url || '';
        if (!newKey || !newImage) {
          await doRefreshCaptcha();
        } else {
          updateCaptchaState({
            key: newKey,
            imageUrl: newImage,
            loading: false
          });
        }
      } else if (status === HTTP_STATUS.TOO_MANY_REQUESTS || err?.__isExpectedError) {
        currentDeferred?.reject?.(err);
        hideCaptcha();
      } else if (status === HTTP_STATUS.BAD_REQUEST || err?.errors?.captcha_response || err?.detail) {
        updateCaptchaState({
          error: getErrorMessage(err),
          loading: false
        });
      } else {
        currentDeferred?.reject?.(err);
        hideCaptcha();
      }
    }
  }, [captchaState, sendCodePath, updateCaptchaState, hideCaptcha, getErrorMessage, doRefreshCaptcha]);

  const handleCaptchaCancel = useCallback(() => {
    if (captchaState.deferred) {
      try {
        captchaState.deferred.reject(new Error(ERROR_MESSAGES.CANCELLED));
      } catch (_) {}
    }
    hideCaptcha();
  }, [captchaState.deferred, hideCaptcha]);

  const clearCaptchaError = useCallback(() => {
    updateCaptchaState({ error: '' });
  }, [updateCaptchaState]);

  /**
   * 创建发送验证码的函数
   * @param {Function} wrapperFunction - 用于包装的函数 (如 wrapFormikSubmitFunction)
   * @param {Function} errorHandler - 错误处理函数
   * @param {boolean} throwErrors - 是否抛出错误
   * @returns {Function} 包装后的发送验证码函数
   */
  const createSendCodeFunction = useCallback((wrapperFunction, errorHandler, throwErrors = true) => {
    const rawSendCode = async (phoneNumber) => {
      try {
        const normalizedPhone = typeof phoneNumber === 'string' ? phoneNumber.trim() : phoneNumber;
        await sendCode(sendCodePath, { phone: normalizedPhone });
      } catch (err) {
        const status = err?.response?.status;
        if (status === HTTP_STATUS.PRECONDITION_REQUIRED) {
          const data = err?.response?.data?.data || {};
          
          return new Promise((resolve, reject) => {
            const nextDeferred = { resolve, reject };
            deferredRef.current = nextDeferred;
            updateCaptchaState({
              visible: true,
              pendingPhone: phoneNumber,
              key: data.key || '',
              imageUrl: data.image_url || '',
              error: '',
              deferred: nextDeferred
            });
          });
        }
        throw err;
      }
    };

    return wrapperFunction(rawSendCode, errorHandler, throwErrors);
  }, [sendCodePath, updateCaptchaState]);

  return {
    captchaState,
    
    confirmCaptcha,
    doRefreshCaptcha,
    hideCaptcha,
    handleCaptchaCancel,
    clearCaptchaError,
    
    createSendCodeFunction,
    
    updateCaptchaState,
    getErrorMessage
  };
};
